import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PaperPlaneRight, ChatCircleDots, Users, ArrowLeft, Plus, X, Microphone, MicrophoneSlash, Paperclip,
  Image as ImageIcon, FileArrowUp, FlagPennant, Check, Checks, House
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDialog } from "../components/Dialog";

const MAX_ATTACH = 4 * 1024 * 1024;
const MAX_VOICE_SECONDS = 90;
const LOUNGE_ID = "LOUNGE";

function fmtClock(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function dayLabel(iso) {
  const d = new Date(iso); const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = (startOf(now) - startOf(d)) / 86400000;
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return d.toLocaleDateString([], { weekday: "long" });
  return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}
function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function fmtDuration(ms) {
  if (!ms) return "";
  const s = Math.round(ms / 1000); const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export default function Chat() {
  const { user } = useAuth();
  const { notify } = useDialog();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState(null);
  const [urgent, setUrgent] = useState(false);
  const [showPlus, setShowPlus] = useState(false);
  const [pendingAttach, setPendingAttach] = useState(null);
  const [recording, setRecording] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const imgInputRef = useRef();
  const endRef = useRef();

  useEffect(() => {
    const t = params.get("thread");
    if (t) setActive(t);
  }, [params]);

  async function loadThreads() {
    if (!user) return;
    try { const { data } = await api.get("/api/chat/threads"); setThreads(data.items || []); } catch {}
  }
  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, [user?.id, active]);

  // Residents skip the list and land straight in their office thread (Society Manager).
  useEffect(() => {
    if (active || !user) return;
    const isStaffUser = ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user.role);
    if (!isStaffUser) {
      const office = threads.find((t) => !t.isLounge);
      if (office) setActive(office.thread);
    }
    // eslint-disable-next-line
  }, [threads, user, active]);

  async function loadMessages() {
    if (!active) return;
    const q = active === LOUNGE_ID ? `?flatId=${LOUNGE_ID}` : `?flatId=${encodeURIComponent(active)}`;
    const { data } = await api.get(`/api/chat/messages${q}`);
    setItems(data.items);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 30);
  }
  useEffect(() => { if (active) loadMessages(); /* eslint-disable-next-line */ }, [active, user?.id]);

  function openThread(t) { setActive(t); setParams(t === LOUNGE_ID ? { thread: LOUNGE_ID } : { thread: t }, { replace: true }); }
  function closeThread() { setActive(null); setParams({}, { replace: true }); }

  async function send(extraAttach) {
    const attachment = extraAttach || pendingAttach;
    if (!text.trim() && !attachment) return;
    if (sending) return;
    setSending(true);
    try {
      const q = `?flatId=${encodeURIComponent(active)}`;
      await api.post(`/api/chat/messages${q}`, {
        body: text.trim(), urgent, replyTo: reply?.id || null,
        attachment: attachment ? {
          kind: attachment.kind, name: attachment.name, mime: attachment.mime,
          dataBase64: attachment.dataBase64, durationMs: attachment.durationMs || null,
        } : null,
      });
      setText(""); setReply(null); setUrgent(false); setPendingAttach(null); setShowPlus(false);
      await loadMessages(); loadThreads();
    } catch (e) {
      if (e?.response?.data?.detail === "attachment_too_large") notify({ title: "Attachment too large", message: "Attachments must be 4 MB or smaller." });
    } finally { setSending(false); }
  }

  async function toggleUrgent(msg) {
    try { await api.post(`/api/chat/messages/${msg.id}/urgent`); await loadMessages(); } catch {}
  }

  function pickFile(kind) {
    setShowPlus(false);
    (kind === "IMAGE" ? imgInputRef : fileInputRef).current?.click();
  }
  async function onFileChosen(e, kind) {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > MAX_ATTACH) { notify({ title: "File too large", message: "Please choose a file 4 MB or smaller." }); return; }
    const b64 = await fileToBase64(f);
    setPendingAttach({ kind, name: f.name, mime: f.type || "application/octet-stream", dataBase64: b64, size: f.size });
  }

  const grouped = useMemo(() => {
    const out = []; let lastDay = null;
    for (const m of items) {
      const day = dayLabel(m.createdAt);
      if (day !== lastDay) { out.push({ kind: "sep", id: `sep-${day}-${m.id}`, label: day }); lastDay = day; }
      out.push({ kind: "msg", id: m.id, msg: m });
    }
    return out;
  }, [items]);

  const isStaffUser = user && ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user.role);
  const visibleThreads = isStaffUser ? threads : threads.filter((t) => !t.isLounge);
  const meta = threads.find((t) => t.thread === active);

  return (
    <div className="lg:flex lg:h-[calc(100dvh-9rem)] lg:gap-4" data-testid="chat-screen">
      {/* Left rail (desktop) / full screen (mobile, no thread open) */}
      <div className={`lg:block lg:w-80 lg:shrink-0 lg:overflow-y-auto lg:rounded-2xl lg:border lg:border-white/10 lg:bg-white/[0.02] lg:p-3 ${active ? "hidden" : "block"}`}>
        <ThreadList threads={visibleThreads} onOpen={openThread} selected={active} />
      </div>

      {/* Conversation pane */}
      <div className={`min-w-0 lg:flex-1 ${active ? "block" : "hidden lg:block"}`}>
        {active ? (
          <ChatRoom
            twoPane
            title={active === LOUNGE_ID ? "Society Lounge" : `Flat ${active}`}
            subtitle={active === LOUNGE_ID ? "Everyone in the society" : "Office thread"}
            isLounge={active === LOUNGE_ID}
            meta={meta}
            onBack={() => { if (isStaffUser) closeThread(); else navigate("/"); }}
            currentUserId={user?.id}
            grouped={grouped}
            reply={reply} setReply={setReply}
            urgent={urgent} setUrgent={setUrgent}
            text={text} setText={setText}
            send={send} sending={sending}
            showPlus={showPlus} setShowPlus={setShowPlus}
            pendingAttach={pendingAttach} setPendingAttach={setPendingAttach}
            recording={recording} setRecording={setRecording}
            pickFile={pickFile} fileInputRef={fileInputRef} imgInputRef={imgInputRef}
            onFileChosen={onFileChosen}
            toggleUrgent={toggleUrgent}
            endRef={endRef}
          />
        ) : (
          <div className="hidden h-full flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02] text-center lg:flex">
            <ChatCircleDots size={44} className="text-white/25" weight="duotone" />
            <p className="mt-3 text-sm font-semibold text-white/50">Select a conversation</p>
            <p className="text-xs text-white/35">Pick a thread on the left to start chatting.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej; r.readAsDataURL(file);
  });
}

// ── Thread list (all roles) ──
function ThreadList({ threads, onOpen, selected }) {
  const lounge = threads.find((t) => t.isLounge);
  const others = threads.filter((t) => !t.isLounge);
  return (
    <div className="font-sans" data-testid="chat-screen">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Chat</p>
        <h1 className="font-display text-3xl font-extrabold text-white">Conversations</h1>
      </div>

      {lounge && (
        <button onClick={() => onOpen(LOUNGE_ID)} data-testid="chat-open-LOUNGE"
          className={`card mt-4 flex w-full items-center gap-3 p-3.5 text-left hover:border-white/20 relative overflow-hidden ${selected === LOUNGE_ID ? "border-neon-cyan/60 bg-neon-cyan/5" : ""}`}>
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-neon-lime/10 blur-3xl" />
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-neon-cyan to-neon-lime text-black shadow-glow-cyan">
            <House size={22} weight="duotone" />
          </div>
          <div className="relative min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="truncate font-display text-[15px] font-bold text-white">Society Lounge</p>
              <span className="chip border-neon-lime/40 text-neon-lime !py-0 !px-1.5 !text-[9px]">GROUP</span>
            </div>
            <p className="truncate text-[12px] text-white/55">{lounge.last ? `${lounge.last.fromName}: ${lounge.last.body || "📎 attachment"}` : "Be the first to say hi to your neighbours."}</p>
          </div>
          {lounge.unread > 0 && <span className="rounded-full bg-neon-cyan px-2 py-0.5 text-[10px] font-bold text-black shadow-glow-cyan">{lounge.unread}</span>}
        </button>
      )}

      <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">Office threads</p>
      <div className="mt-2 grid gap-2 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-1">
        {others.length === 0 ? (
          <div className="card flex flex-col items-center gap-2 p-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-neon-cyan/15 text-neon-cyan"><Users size={22} weight="duotone" /></div>
            <p className="text-[13px] text-white/55">No office threads yet.</p>
          </div>
        ) : others.map((t) => (
          <button key={t.thread} onClick={() => onOpen(t.thread)} data-testid={`chat-open-${t.thread}`}
            className={`card flex w-full items-center gap-3 p-3.5 text-left hover:border-white/20 ${selected === t.thread ? "border-neon-cyan/60 bg-neon-cyan/5" : t.urgent > 0 ? "border-neon-pink/40" : ""}`}>
            <div className={`relative flex h-11 w-11 items-center justify-center rounded-2xl font-mono text-[12px] font-bold ${t.urgent > 0 ? "bg-neon-pink/15 text-neon-pink" : "bg-neon-cyan/15 text-neon-cyan"}`}>
              {t.thread}
              {t.urgent > 0 && <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-neon-pink text-[9px] font-bold text-white shadow-glow-pink"><FlagPennant size={9} weight="fill" /></span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-[14px] font-semibold text-white">{t.last?.fromName || t.thread}</p>
                {t.last && <span className="text-[10px] text-white/40">· {fmtClock(t.last.createdAt)}</span>}
              </div>
              <p className="truncate text-[12px] text-white/55">{t.last ? (t.last.body || (t.last.attachment ? `📎 ${t.last.attachment.kind.toLowerCase()}` : "…")) : "No messages yet."}</p>
            </div>
            {t.unread > 0 && <span className="rounded-full bg-neon-cyan px-2 py-0.5 text-[10px] font-bold text-black shadow-glow-cyan">{t.unread}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Chat Room (full-screen) ──
function ChatRoom({
  twoPane, title, subtitle, isLounge, meta, onBack, currentUserId, grouped,
  reply, setReply, urgent, setUrgent, text, setText,
  send, sending, showPlus, setShowPlus, recording, setRecording,
  pendingAttach, setPendingAttach, pickFile, fileInputRef, imgInputRef, onFileChosen, toggleUrgent, endRef,
}) {
  return (
    <div className={twoPane
        ? "fixed inset-0 z-[60] flex flex-col lg:relative lg:inset-auto lg:z-auto lg:h-full lg:overflow-hidden lg:rounded-2xl lg:border lg:border-white/10"
        : "fixed inset-0 z-[60] flex flex-col"}
      style={{ background: "var(--shell)" }} data-testid="chat-room">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-ink-900/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2 px-3 py-3">
          <button onClick={onBack} data-testid="chat-back" className={`rounded-full bg-white/5 p-2 text-white/80 hover:text-white ${twoPane ? "lg:hidden" : ""}`}><ArrowLeft size={18} weight="bold" /></button>
          <div className={`flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-extrabold text-black ${isLounge ? "bg-gradient-to-br from-neon-lime to-neon-cyan" : "bg-gradient-to-br from-neon-cyan to-neon-lime"}`}>
            {isLounge ? <House size={18} weight="duotone" /> : title[0]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-[15px] font-bold text-white">{title}</p>
            <p className="truncate text-[11px] text-white/55">{subtitle}</p>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto" data-testid="chat-messages">
        <div className="mx-auto max-w-3xl px-3 py-4 space-y-1.5">
          {grouped.map((g) =>
            g.kind === "sep" ? (
              <div key={g.id} className="my-3 flex justify-center" data-testid={`chat-sep-${g.label.toLowerCase().replace(/ /g, "-")}`}>
                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/55">{g.label}</span>
              </div>
            ) : (
              <MessageBubble key={g.id} m={g.msg} mine={g.msg.fromId === currentUserId}
                onReply={(m) => setReply(m)} onUrgent={(m) => toggleUrgent(m)} showFrom={isLounge || g.msg.fromId !== currentUserId} />
            )
          )}
          <div ref={endRef} />
        </div>
      </main>

      {reply && (
        <div className="border-t border-white/10 bg-ink-900/95 px-3 py-2" data-testid="reply-preview">
          <div className="mx-auto flex max-w-3xl items-center gap-2 rounded-xl border-l-4 border-neon-cyan bg-white/[0.03] px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neon-cyan">Replying to {reply.fromName}</p>
              <p className="truncate text-[12px] text-white/70">{reply.body || (reply.attachment ? `📎 ${reply.attachment.kind.toLowerCase()}` : "…")}</p>
            </div>
            <button onClick={() => setReply(null)} className="rounded-full bg-white/5 p-1 text-white/55 hover:text-white" data-testid="reply-cancel"><X size={14} weight="bold" /></button>
          </div>
        </div>
      )}

      {pendingAttach && !recording && (
        <div className="border-t border-white/10 bg-ink-900/95 px-3 py-2" data-testid="pending-attach">
          <div className="mx-auto flex max-w-3xl items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
            <Paperclip size={14} className="text-neon-cyan" weight="bold" />
            <span className="truncate text-[12px] text-white">{pendingAttach.name}</span>
            <span className="text-[10px] uppercase tracking-wider text-white/45">{fmtBytes(pendingAttach.size)}</span>
            <button onClick={() => setPendingAttach(null)} className="ml-auto rounded-full bg-white/5 p-1 text-white/55" data-testid="attach-cancel"><X size={14} weight="bold" /></button>
          </div>
        </div>
      )}

      {recording && (
        <VoiceRecorderBar
          onCancel={() => setRecording(false)}
          onComplete={(attachment) => { setRecording(false); send(attachment); }}
        />
      )}

      {!recording && (
        <footer className="border-t border-white/10 bg-ink-900/95 pb-safe">
          <div className="mx-auto max-w-3xl px-3 py-2.5">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2" data-testid="chat-composer">
              {/* + menu (NO voice option here) */}
              <div className="relative">
                <button type="button" onClick={() => setShowPlus((v) => !v)} data-testid="chat-plus"
                  className={`flex h-11 w-11 items-center justify-center rounded-full transition ${showPlus ? "bg-neon-cyan text-black shadow-glow-cyan rotate-45" : "border border-white/10 bg-white/5 text-white/80"}`}>
                  <Plus size={20} weight="bold" />
                </button>
                <AnimatePresence>
                  {showPlus && (
                    <motion.div initial={{ opacity: 0, y: 6, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6, scale: 0.96 }}
                      transition={{ duration: 0.16 }} data-testid="plus-menu"
                      className="absolute bottom-14 left-0 w-44 overflow-hidden rounded-2xl border border-white/10 bg-ink-900/95 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.6)]">
                      <button type="button" data-testid="plus-image" onClick={() => pickFile("IMAGE")}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.06]">
                        <ImageIcon size={16} weight="fill" className="text-neon-cyan" /> Photo
                      </button>
                      <button type="button" data-testid="plus-file" onClick={() => pickFile("FILE")}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.06]">
                        <FileArrowUp size={16} weight="fill" className="text-neon-lime" /> File
                      </button>
                      <button type="button" data-testid="plus-urgent" onClick={() => { setUrgent((v) => !v); setShowPlus(false); }}
                        className="flex w-full items-center gap-2.5 border-t border-white/5 px-3 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.06]">
                        <FlagPennant size={16} weight="fill" className={urgent ? "text-neon-pink" : "text-white/55"} />
                        Mark urgent {urgent && <Check size={12} weight="bold" className="text-neon-cyan" />}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mic button separately */}
              <button type="button" onClick={() => setRecording(true)} data-testid="chat-mic"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-neon-pink hover:bg-white/10">
                <Microphone size={20} weight="fill" />
              </button>

              <input ref={imgInputRef} type="file" accept="image/*" onChange={(e) => onFileChosen(e, "IMAGE")} className="hidden" />
              <input ref={fileInputRef} type="file" onChange={(e) => onFileChosen(e, "FILE")} className="hidden" />

              <div className="relative flex-1">
                <textarea data-testid="chat-input" rows={1}
                  className={`field !py-3 max-h-32 resize-none ${urgent ? "!border-neon-pink !ring-2 !ring-neon-pink/30" : ""}`}
                  placeholder={urgent ? "URGENT message — type here…" : "Type a message…"}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                {urgent && (
                  <span data-testid="urgent-tag" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full bg-neon-pink px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-white shadow-glow-pink">
                    <FlagPennant size={9} weight="fill" /> Urgent
                  </span>
                )}
              </div>

              <button data-testid="chat-send" className="btn-primary !px-4 !py-3" disabled={(!text.trim() && !pendingAttach) || sending}>
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </form>
          </div>
        </footer>
      )}
    </div>
  );
}

// ── Voice Recorder Bar (90s max, Stop & Send) ──
function VoiceRecorderBar({ onCancel, onComplete }) {
  const [state, setState] = useState("idle"); // idle | recording | error
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 64000 });
      recRef.current = rec; chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        clearInterval(timerRef.current);
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        const b = new Blob(chunksRef.current, { type: mime });
        if (b.size === 0) { setError("No audio captured."); setState("error"); return; }
        if (b.size > MAX_ATTACH) { setError("Recording exceeds 4 MB."); setState("error"); return; }
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = String(reader.result).split(",")[1] || "";
          onComplete({
            kind: "AUDIO", name: `voice-${Date.now()}.webm`, mime: b.type || "audio/webm",
            dataBase64: base64, durationMs: seconds * 1000, size: b.size,
          });
        };
        reader.readAsDataURL(b);
      };
      rec.start();
      setState("recording"); setSeconds(0); setError(null);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_VOICE_SECONDS) {
            // auto-stop & send at 90s
            try { rec.state !== "inactive" && rec.stop(); } catch {}
            return MAX_VOICE_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) { setError("Microphone permission denied."); setState("error"); }
  }

  function stopAndSend() {
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch {}
  }

  function cancel() {
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    clearInterval(timerRef.current);
    onCancel();
  }

  useEffect(() => { start(); return () => { try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {} clearInterval(timerRef.current); }; /* eslint-disable-next-line */ }, []);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const pct = Math.min(100, (seconds / MAX_VOICE_SECONDS) * 100);

  return (
    <div className="border-t border-white/10 bg-ink-900/95 px-3 py-3 pb-safe" data-testid="voice-recorder-bar">
      <div className="mx-auto flex max-w-3xl flex-col gap-2">
        {state === "error" ? (
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-neon-pink/20 text-neon-pink"><MicrophoneSlash weight="fill" size={18} /></span>
            <p className="flex-1 text-[12px] text-neon-pink">{error}</p>
            <button type="button" onClick={cancel} className="rounded-full bg-white/5 p-2 text-white/60"><X size={16} weight="bold" /></button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-neon-pink text-white shadow-glow-pink animate-pulse-soft"><Microphone weight="fill" size={18} /></span>
              <div className="flex-1">
                <p className="text-[11px] font-bold uppercase tracking-wider text-neon-pink">Recording voice note</p>
                <p className="font-mono text-[15px] font-bold text-white">{mm}:{ss} <span className="text-[11px] font-normal text-white/55">/ 01:30 max</span></p>
              </div>
              <button type="button" data-testid="voice-cancel" onClick={cancel}
                className="rounded-full bg-white/5 p-2 text-white/70 hover:text-white"><X size={18} weight="bold" /></button>
              <button type="button" data-testid="voice-stop-send" onClick={stopAndSend}
                className="inline-flex items-center gap-1.5 rounded-full bg-neon-cyan px-4 py-2 text-sm font-bold text-black shadow-glow-cyan">
                <PaperPlaneRight size={14} weight="fill" /> Stop & Send
              </button>
            </div>
            <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
              <div className="h-full bg-neon-pink transition-[width]" style={{ width: `${pct}%` }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Message Bubble (swipe-to-reply + flag) ──
function MessageBubble({ m, mine, onReply, onUrgent, showFrom = true }) {
  const [dx, setDx] = useState(0);
  const startRef = useRef(null);
  const reactedRef = useRef(false);

  function onPointerDown(e) { startRef.current = { x: e.clientX, y: e.clientY }; reactedRef.current = false; }
  function onPointerMove(e) {
    if (!startRef.current) return;
    const ddx = e.clientX - startRef.current.x; const ddy = e.clientY - startRef.current.y;
    if (Math.abs(ddx) > Math.abs(ddy)) {
      const allowed = mine ? Math.max(-72, Math.min(0, ddx)) : Math.max(0, Math.min(72, ddx));
      setDx(allowed);
      if (!reactedRef.current && Math.abs(allowed) > 56) { reactedRef.current = true; onReply(m); }
    }
  }
  function onPointerUp() { setDx(0); startRef.current = null; }

  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className="swipe-wrap relative max-w-[80%]"
        style={{ transform: `translateX(${dx}px)`, transition: dx === 0 ? "transform 180ms" : "none" }}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        {Math.abs(dx) > 8 && (
          <span className={`absolute top-1/2 -translate-y-1/2 ${mine ? "right-[-44px]" : "left-[-44px]"} flex h-8 w-8 items-center justify-center rounded-full bg-neon-cyan/20 text-neon-cyan`}>
            <ChatCircleDots size={16} weight="fill" />
          </span>
        )}

        <div className={`rounded-2xl px-3 py-2 text-[14px] ${mine ? "bubble-mine" : "bubble-theirs"} ${m.urgent ? "bubble-urgent" : ""}`}>
          {m.urgent && (
            <div className={`mb-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider ${mine ? "bg-black/10 text-neon-pink" : "bg-neon-pink/15 text-neon-pink"}`}>
              <FlagPennant size={10} weight="fill" /> Urgent
            </div>
          )}
          {showFrom && !mine && <p className={`mb-0.5 text-[10px] font-bold uppercase tracking-wider ${m.urgent ? "text-neon-pink/90" : "opacity-60"}`}>{m.fromName} · {m.fromRole}</p>}

          {m.replyTo && (
            <div className={`mb-1 border-l-2 ${mine ? "border-black/30 bg-black/5" : "border-neon-cyan bg-white/5"} px-2 py-1`}>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{m.replyTo.fromName}</p>
              <p className="truncate text-[11px] opacity-80">{m.replyTo.body || (m.replyTo.attachmentKind ? `📎 ${m.replyTo.attachmentKind.toLowerCase()}` : "…")}</p>
            </div>
          )}

          {m.attachment && <Attachment a={m.attachment} mine={mine} />}
          {m.body && <p className="whitespace-pre-wrap leading-snug">{m.body}</p>}

          <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${mine ? "opacity-65" : "opacity-55"}`}>
            <span>{fmtClock(m.createdAt)}</span>
            {mine && (m.readAt ? <Checks size={13} weight="bold" className="text-blue-700" /> : m.deliveredAt ? <Checks size={13} weight="bold" /> : <Check size={13} weight="bold" />)}
          </div>
        </div>

        <div className={`pointer-events-none absolute -top-2 ${mine ? "left-2" : "right-2"} opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100`}>
          <button onClick={() => onUrgent(m)} title="Toggle urgent" data-testid={`msg-flag-${m.id}`}
            className={`rounded-full px-2 py-1 text-[10px] font-bold ${m.urgent ? "bg-neon-pink text-white" : "border border-white/15 bg-ink-900 text-white/70"}`}>
            <FlagPennant size={11} weight="fill" className="-mt-0.5 mr-1 inline" />{m.urgent ? "Urgent" : "Flag"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Attachment({ a, mine }) {
  if (!a) return null;
  const src = a.dataBase64 ? `data:${a.mime};base64,${a.dataBase64}` : "";
  if (a.kind === "IMAGE") return <img src={src} alt={a.name} className="mb-1 max-h-72 w-full rounded-xl object-cover" />;
  if (a.kind === "AUDIO") return (
    <div className={`mb-1 flex items-center gap-2 rounded-xl px-2 py-1.5 ${mine ? "bg-black/10" : "bg-white/[0.04]"}`}>
      <Microphone size={16} weight="fill" />
      <audio controls src={src} className="h-8 flex-1" />
      {a.durationMs ? <span className="text-[10px] opacity-70">{fmtDuration(a.durationMs)}</span> : null}
    </div>
  );
  return (
    <a href={src} download={a.name} className={`mb-1 flex items-center gap-2 rounded-xl px-2 py-2 ${mine ? "bg-black/10" : "bg-white/[0.04]"}`}>
      <FileArrowUp size={16} weight="fill" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-semibold">{a.name}</p>
        <p className="text-[10px] opacity-65">{fmtBytes(a.size)}</p>
      </div>
    </a>
  );
}
